'use client';

import type { ReactNode } from 'react';

import { Button } from '@teamsparta/stack-button';
import { InfoError } from '@teamsparta/stack-icons';
import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';

import * as S from './errorScreen.styles';

interface ErrorScreenAction {
  label: string;
  onClick: () => void;
}

interface ErrorScreenProps {
  title: string;
  description: ReactNode;
  primaryAction: ErrorScreenAction;
  /** 옵셔널 보조 액션 (예: error.tsx의 "다시 시도"). primary 아래에 outline으로 노출. */
  secondaryAction?: ErrorScreenAction;
  /** 기본 아이콘은 InfoError. 케이스별로 다른 아이콘이 필요할 때만 주입. */
  icon?: ReactNode;
}

/**
 * 앱 전반에서 재사용하는 에러/안내 fallback 화면 (not-found·error·slug 누락 진입 등).
 * 도메인 의존성 없는 dumb 컴포넌트 — 복귀 로직은 호출 측에서 주입한다.
 */
export function ErrorScreen({
  title,
  description,
  primaryAction,
  secondaryAction,
  icon,
}: ErrorScreenProps) {
  return (
    <S.Main>
      <S.Content>
        <S.IconBadge>
          {icon ?? <InfoError size={24} color={vars.icon.quaternary} variant="fill" />}
        </S.IconBadge>
        <S.Texts>
          <Text as="h1" font="title2" color={vars.neutral[100]} align="center">
            {title}
          </Text>
          <Text as="p" font="bodyM" color={vars.neutral[70]} align="center">
            {description}
          </Text>
        </S.Texts>
        <S.Actions>
          <Button
            variant="solid"
            colorScheme="primary"
            size="lg"
            fullWidth
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
          {secondaryAction && (
            <Button
              variant="outline"
              colorScheme="primary"
              size="lg"
              fullWidth
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </S.Actions>
      </S.Content>
    </S.Main>
  );
}
